/* eslint-disable jsx-a11y/anchor-is-valid */
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaCashRegister } from "react-icons/fa";
import { DateTime } from "luxon";
import useSWR from "swr";

import { api } from "../../api/api";
import { NavBar } from "../../components/NavBar";
import { ModalSafe } from "../../components/Modals/Safe";
import { OpenSafe } from "../../components/Modals/OpenSafe";
import { GlobalContext } from "../../context/GlobalContext";
import { SELECT_ORDER } from "../../stores/reducers/OrderReducers";
import { SET_SAFE } from "../../stores/reducers/SafeReducers";
import { maskCurrency, TypePayment } from "../../utils/utils";
import { ModalCloseSafe } from "./components/ModalCloseSafe";
import "./style.css";

const INITIAL_FILTERS = {
  type: "",
  typePayment: "",
  startDate: "",
  endDate: "",
};

const EMPTY_TOTALS = {
  entrada: 0,
  saida: 0,
  sangria: 0,
  total: 0,
};

export function SafePage() {
  const { isAccess, openModalOrder } = useContext(GlobalContext);
  const safes = useSelector((state) => state.safe.data);
  const dispatch = useDispatch();
  const safeChangesRef = useRef(safes);

  const [isOpen, setIsOpen] = useState(false);
  const [viewRegisters, setViewRegisters] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 450);
    return () => clearTimeout(timeout);
  }, [search]);

  const requestParams = {
    page,
    limit,
    ...(filters.type && { type: filters.type }),
    ...(filters.typePayment && { typePayment: filters.typePayment }),
    ...(filters.startDate && { startDate: filters.startDate }),
    ...(filters.endDate && { endDate: filters.endDate }),
    ...(debouncedSearch && { search: debouncedSearch }),
  };

  const {
    data: safeResponse,
    error,
    isLoading,
    isValidating,
    mutate: mutateSafe,
  } = useSWR(
    ["/safe", requestParams],
    async ([url, params]) => {
      const response = await api.get(url, { params });
      return response.data;
    },
    { keepPreviousData: true }
  );

  const movements = safeResponse?.data ?? [];
  const pagination = safeResponse?.pagination ?? {
    page,
    limit,
    total: 0,
    totalPages: 0,
  };
  const responseTotals = safeResponse?.totals ?? EMPTY_TOTALS;
  const totals = {
    entrada: Number(responseTotals.entrada ?? 0),
    saida: Number(responseTotals.saida ?? 0),
    sangria: Number(responseTotals.sangria ?? 0),
    total: Number(responseTotals.total ?? ((responseTotals.entrada ?? 0) - (responseTotals.saida ?? 0))),
  };

  useEffect(() => {
    if (safeChangesRef.current !== safes) {
      safeChangesRef.current = safes;
      mutateSafe();
    }
  }, [mutateSafe, safes]);

  async function openOrder(id) {
    const response = await api.get(`/orders/${id}`);
    dispatch(SELECT_ORDER(response.data));
    openModalOrder();
  }

  async function verifySafeIsOpen() {
    try {
      const response = await api.get("/safe/is/open");
      setIsOpen(Boolean(response?.data));
    } catch (requestError) {
      setIsOpen(false);
    }
  }

  async function reloadSafe() {
    await verifySafeIsOpen();
    await mutateSafe();
  }

  useEffect(() => {
    verifySafeIsOpen();
  }, []);

  function updateFilter(key, value) {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }

  function updateSearch(value) {
    setSearch(value);
    setPage(1);
  }

  function updateLimit(value) {
    setLimit(Number(value));
    setPage(1);
  }

  const totalPages = Number(pagination.totalPages ?? 0);
  const currentPage = Number(pagination.page ?? page);

  return (
    <NavBar>
      <ModalSafe />
      <OpenSafe reload={reloadSafe} viewRegisters={viewRegisters} isOpen={isOpen} />
      <ModalCloseSafe reload={reloadSafe} />

      <div className="container-fluid w1 safe-page">
        <div className="mt-3 p-2 bg-dark-blue-1 rounded fw-bold d-flex justify-content-between align-items-center border mb-3 safe-header">
          <p className="text-white safe-title"><FaCashRegister className="mb-1" color="white" size={22} /> Caixa</p>
          <div className="safe-header-actions">
            {!isOpen && (
              <button className="btn btn-sm btn-warning" data-bs-toggle="modal" data-bs-target="#modalIsOpenSafe" onClick={() => setViewRegisters(false)}>
                <FaCashRegister className="mb-1" color="white" /> Abrir caixa
              </button>
            )}

            <button className="btn btn-sm btn-primary" data-bs-toggle="modal" data-bs-target="#modalIsOpenSafe" onClick={() => setViewRegisters(true)}>
              <FaCashRegister className="mb-1" color="white" /> Registros do Caixa
            </button>

            {isOpen && (isAccess("caixa", "open_safe") || isAccess("caixa", "close_safe")) && (
              <button className="btn btn-sm bg-white text-dark" data-bs-toggle="modal" data-bs-target="#modalCloseSafe">
                <FaCashRegister className="mb-1" color="black" /> Fechar Caixa
              </button>
            )}
          </div>
        </div>

        <div className="safe-toolbar">
          <div className="safe-filters">
            <div className="safe-filter-field">
              <label htmlFor="safe-type">Movimento</label>
              <select id="safe-type" className="form-select form-select-sm" value={filters.type} onChange={(event) => updateFilter("type", event.target.value)}>
                <option value="">Todos</option>
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
                <option value="sangria">Sangria</option>
              </select>
            </div>

            <div className="safe-filter-field">
              <label htmlFor="safe-payment">Pagamento</label>
              <select id="safe-payment" className="form-select form-select-sm" value={filters.typePayment} onChange={(event) => updateFilter("typePayment", event.target.value)}>
                <option value="">Todos</option>
                <option value={TypePayment.money}>Dinheiro</option>
                <option value={TypePayment.card_credit}>Crédito</option>
                <option value={TypePayment.card_debit}>Débito</option>
                <option value={TypePayment.pix}>PIX</option>
                <option value={TypePayment.ifood}>Ifood</option>
              </select>
            </div>

            <div className="safe-filter-field">
              <label htmlFor="safe-start-date">Data inicial</label>
              <input id="safe-start-date" type="date" className="form-control form-control-sm" value={filters.startDate} onChange={(event) => updateFilter("startDate", event.target.value)} />
            </div>

            <div className="safe-filter-field">
              <label htmlFor="safe-end-date">Data final</label>
              <input id="safe-end-date" type="date" className="form-control form-control-sm" value={filters.endDate} onChange={(event) => updateFilter("endDate", event.target.value)} />
            </div>

            <div className="safe-filter-field safe-search-field">
              <label htmlFor="safe-search">Descrição ou pedido</label>
              <input id="safe-search" type="search" className="form-control form-control-sm" placeholder="Buscar..." value={search} onChange={(event) => updateSearch(event.target.value)} />
            </div>

            <div className="safe-filter-field safe-limit-field">
              <label htmlFor="safe-limit">Itens</label>
              <select id="safe-limit" className="form-select form-select-sm" value={limit} onChange={(event) => updateLimit(event.target.value)}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {isAccess("caixa", "create") && (
            <button className="btn btn-sm bg-dark text-white btn-circle safe-create-button" aria-label="Criar movimento" data-bs-toggle="modal" data-bs-target="#modalSafe">
              <i className="fas fa-plus"></i>
            </button>
          )}
        </div>

        <div className="safe-cards">
          <div className="safe-card safe-card-entry"><span>Entradas</span><strong>{maskCurrency(totals.entrada)}</strong></div>
          <div className="safe-card safe-card-exit"><span>Saídas</span><strong>{maskCurrency(totals.saida)}</strong></div>
          <div className="safe-card safe-card-withdrawal"><span>Sangrias</span><strong>{maskCurrency(totals.sangria)}</strong></div>
          <div className="safe-card safe-card-total"><span>Total</span><strong>{maskCurrency(totals.total)}</strong></div>
        </div>

        {isValidating && (
          <div className="safe-loading" role="status">
            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
            Atualizando movimentações...
          </div>
        )}

        {error && (
          <div className="safe-feedback safe-error" role="alert">
            <span>Não foi possível carregar as movimentações.</span>
            <button className="btn btn-sm btn-outline-danger" onClick={() => mutateSafe()}>Tentar novamente</button>
          </div>
        )}

        {!error && !isLoading && movements.length === 0 && (
          <div className="safe-feedback">Nenhuma movimentação encontrada.</div>
        )}

        {!error && movements.length > 0 && (
          <div className="table-responsive safe-table-wrapper">
            <table className="table table-borderless table-striped table-sm safe-table">
              <thead className="text-white">
                <tr>
                  <th className="text-center">Tipo</th>
                  <th className="text-center">Data</th>
                  <th className="text-center">Valor</th>
                  <th className="text-center">Forma de Pagamento</th>
                  <th className="text-center">Descrição</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.id} className="p-2 text-center">
                    <td className={`fw-bold safe-movement-${movement.type}`}>{String(movement.type).toUpperCase()}</td>
                    <td>{DateTime.fromISO(movement.date.substring(0, 10)).toFormat("dd/MM/yyyy")}</td>
                    <td>{maskCurrency(movement.pricing)}</td>
                    <td>{movement.typePayment}</td>
                    <td>{movement?.description?.includes("Ifood") ? "Pedido Ifood" : movement.description}</td>
                    <td>
                      <div className="dropdown">
                        <button className="btn dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" aria-label="Ações da movimentação">
                          <svg viewBox="0 0 16 16" width="1em" height="1em" focusable="false" role="img" aria-label="Opções" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi-gear-fill contentActions b-icon bi"><path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872l-.1-.34zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z"></path></svg>
                        </button>
                        <ul className="dropdown-menu">
                          {movement.orderId && <li><a className="dropdown-item" rel="noreferrer" onClick={() => openOrder(movement.orderId)}>Pedido</a></li>}
                          <li>
                            <a className="dropdown-item" data-bs-toggle="modal" data-bs-target="#modalSafe" onClick={() => dispatch(SET_SAFE({
                              id: movement.id,
                              type: movement.type,
                              pricing: movement.pricing,
                              orderId: movement.orderId,
                              description: movement.description,
                              date: DateTime.fromISO(movement.date).toFormat("yyyy-MM-dd"),
                              typePayment: movement.typePayment,
                            }))}>Detalhes</a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error && !isLoading && (
          <div className="safe-pagination" aria-label="Paginação das movimentações">
            <span>{pagination.total ?? 0} registro(s)</span>
            <div className="safe-pagination-controls">
              <button className="btn btn-sm btn-outline-secondary" disabled={currentPage <= 1 || isValidating} onClick={() => setPage((current) => Math.max(1, current - 1))}>Anterior</button>
              <span>Página {currentPage} de {totalPages}</span>
              <button className="btn btn-sm btn-outline-secondary" disabled={currentPage >= totalPages || isValidating} onClick={() => setPage((current) => current + 1)}>Próxima</button>
            </div>
          </div>
        )}
      </div>
    </NavBar>
  );
}
